import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useSettings } from "@/lib/useSettings";
import Icon from "@/components/Icon";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (!u ? "" : /^https?:\/\//i.test(u) ? u : u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const LINKS = [
  { to: "/", label: "الرئيسية" },
  { to: "/about", label: "من نحن" },
  { to: "/services", label: "خدماتنا" },
  { to: "/products", label: "المنتجات" },
  { to: "/offers", label: "العروض" },
  { to: "/contact", label: "اتصل بنا" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const phone = settings?.phone || "";
  const logo = settings?.logo_url;

  return (
    <header
      dir="rtl"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A1628]/85 border-b border-white/10"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="nav-brand"
          onClick={() => setOpen(false)}
        >
          {logo ? (
            <div className="h-11 md:h-14 rounded-xl bg-[#00E5FF] shadow-[0_0_20px_rgba(0,229,255,0.4)] p-1.5 flex items-center justify-center">
              <img
                src={abs(logo)}
                alt={settings?.company_name_ar || "أمان"}
                className="h-full w-auto max-w-[110px] object-contain"
                data-testid="nav-logo-image"
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#00b8cc] flex items-center justify-center shadow-[0_0_20px_rgba(0,229,255,0.4)]">
              <Icon name="shield-check" className="text-[#0A1628]" size={22} strokeWidth={2.2} />
            </div>
          )}
          <div className="leading-tight">
            <div className="font-heading text-2xl font-bold text-white tracking-tight">أمان</div>
            <div className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-cyan-300/80 whitespace-nowrap">AMAN Security</div>
          </div>
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) =>
            l.hash ? (
              <a
                key={l.to}
                href={l.to}
                className="text-slate-200/85 hover:text-[#00E5FF] transition-colors text-sm font-medium"
                data-testid={`nav-link-${l.label}`}
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-[#00E5FF]" : "text-slate-200/85 hover:text-[#00E5FF]"
                  }`
                }
                data-testid={`nav-link-${l.label}`}
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        {/* CTA / call */}
        <div className="flex items-center gap-3">
          {phone && (
            <a
              href={`tel:${phone}`}
              className="hidden sm:inline-flex items-center gap-2 text-slate-200/80 hover:text-white text-sm font-medium transition-colors"
              data-testid="nav-call"
            >
              <Icon name="phone" size={16} />
              <span dir="ltr">{phone}</span>
            </a>
          )}
          <Link to="/contact" className="hidden md:inline-flex aman-btn-primary text-sm" data-testid="nav-cta-quote">
            <Icon name="send" size={16} strokeWidth={2} />
            اطلب عرض سعر
          </Link>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-white/90 hover:text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            <Icon name={open ? "x" : "menu"} size={24} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0A1628]/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-4">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.hash ? "/" : l.to}
                onClick={() => setOpen(false)}
                className="text-slate-100 py-2 text-base font-medium hover:text-[#00E5FF] transition-colors"
                data-testid={`mnav-link-${l.label}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="aman-btn-primary justify-center text-sm mt-2"
              data-testid="mnav-cta-quote"
            >
              اطلب عرض سعر
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
